<template>
  <div>
    <div
      v-if="loading"
      class="text-center"
    >
      <b-spinner
        label="Small Spinner"
        variant="primary"
      />
    </div>

    <b-alert
      variant="danger"
      :show="loadingError ? true : false"
    >
      <div class="alert-body">
        <p>
          {{ loadingError }}
        </p>
      </div>
    </b-alert>

    <b-table
      v-if="!loading && !loadingError"
      :items="results"
      :fields="tableFields"
      responsive="sm"
      striped
      hover
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      class="compact-table"
      no-local-sorting
      @sort-changed="sortingChanged"
    >
      <template #cell(salesman_name)="data">
        {{ data.item.salesman_name }}
      </template>
      <template #cell(total_target)="data">
        {{ formateNumber(data.item.total_target) }}
      </template>
      <template #cell(total_net_sales)="data">
        {{ formateNumber(data.item.total_net_sales) }}
      </template>
      <template #cell(bonus_amount)="data">
        {{ formateNumber(data.item.bonus_amount) }}
      </template>
      <template #cell(total_party_handling_fees)="data">
        {{ formateNumber(data.item.total_party_handling_fees) }}
      </template>
      <template #cell(delivery_incentive)="data">
        {{ formateNumber(data.item.delivery_incentive) }}
      </template>
      <template #cell(penalty_amount)="data">
        <span class="text-danger">
          {{ formateNumber(data.item.penalty_amount) }}
        </span>
      </template>
      <template #cell(total_salary)="data">
        <span :class="data.item.total_salary < 0 ? 'text-danger' : ''">
          {{ formateNumber(data.item.total_salary) }}
        </span>
      </template>
      <template #cell(actions)="data">
        <feather-icon
          icon="EyeIcon"
          size="16"
          class="cursor-pointer"
          @click="openModal(data.item.salesman_id)"
        />
      </template>
    </b-table>

    <detailed-pagination
      v-if="!loading && !loadingError"
      :total-items="totalItems"
      :per-page="perPage"
      :current-page="currentPage"
      @page-changed="(page) => {currentPage = page}"
    />
    <salary-details-modal
      v-if="selectedSalesmanId"
      v-model="showModal"
      :salesman-id="selectedSalesmanId"
      @modal-closed="selectedSalesmanId = null"
      @onStatusChange="fetchItems"
    />
  </div>
</template>

<script>
import {
  BSpinner, BTable, BAlert, VBTooltip,
} from 'bootstrap-vue'
import axios from 'axios'

import dateMixin from '@/mixins/dateMixin'
import formatMixin from '@/mixins/formatMixin'
import DetailedPagination from '@/components/UI/DetailedPagination.vue'
import getParsedQueryString from '@/utils/headerQueryParser'
import SalaryDetailsModal from './SalaryDetailsModal.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BSpinner,
    BTable,
    BAlert,
    DetailedPagination,
    SalaryDetailsModal,
  },
  mixins: [dateMixin, formatMixin],
  props: {
    filters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      loadingError: null,
      selectedSalesmanId: null,
      showModal: false,
      results: [],
      tableFields: [
        { key: 'salesman_name', label: 'Name', sortable: true },
        {
          key: 'total_target', label: 'Target', sortable: true, class: 'text-right pr-2',
        },
        {
          key: 'total_net_sales', label: 'Net Sales', sortable: true, class: 'text-right pr-2',
        },
        {
          key: 'bonus_amount', label: 'Bonus', sortable: true, class: 'text-right pr-2',
        },
        {
          key: 'total_party_handling_fees', label: 'Party Handling Fees', sortable: true, class: 'text-right pr-2',
        },
        {
          key: 'delivery_incentive', label: 'Delivery Incentive', sortable: true, class: 'text-right pr-2',
        },
        {
          key: 'penalty_amount', label: 'Penalty', sortable: true, class: 'text-right pr-2',
        },
        {
          key: 'total_salary', label: 'Total Salary', sortable: true, class: 'text-right pr-2',
        },
        { key: 'actions', label: 'Actions', sortable: false },
      ],
      currentPage: 1,
      perPage: 50,
      totalItems: 0,
      sortBy: 'salesman_name',
      sortDesc: false,
      submitting: false,
    }
  },
  watch: {
    currentPage() {
      this.fetchItems()
    },
    filters() {
      if (this.currentPage !== 1) {
        this.currentPage = 1
      } else {
        this.fetchItems()
      }
    },
  },
  created() {
    this.fetchItems()
  },
  methods: {
    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.fetchItems()
    },
    openModal(salesmanId) {
      this.selectedSalesmanId = salesmanId
      this.showModal = true
    },
    async fetchItems() {
      try {
        this.loading = true

        const { monthDate, salesman } = this.filters

        const preparedPayload = {
          page: this.currentPage,
          per_page: this.perPage,
          sort_by: this.sortBy,
          sort_desc: this.sortDesc,
          ...(monthDate && { month_date: monthDate }),
          ...(salesman && { salesman }),
        }

        const response = await axios.get(
          `/accounts/salesman-salaries/?${getParsedQueryString(preparedPayload)}`,
        )

        this.results = response.data.results
        this.totalItems = response.data.total_salesmen
        this.loadingError = null
      } catch (error) {
        this.loadingError = error?.response?.data?.error || 'Something went wrong'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
